import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

// iter130 Phase A — Google Sign-In (Google Identity Services)
//
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT
// URLS, THIS BREAKS THE AUTH.
//
// The GIS SDK is loaded from https://accounts.google.com/gsi/client
// via a <script> tag inserted on mount. Once loaded, we render the
// pill-shaped Google button into `container`; on click, Google
// returns an ID token which we POST to `/api/auth/google` where the
// backend does full signature + audience + issuer verification.
//
// The `client_id` comes from the frontend env `REACT_APP_GOOGLE_OAUTH_CLIENT_ID`
// which mirrors the backend `GOOGLE_OAUTH_CLIENT_ID`. If the env is
// missing the button silently unrenders — the login page checks the
// same via /api/auth/providers so both sides stay in sync.
export default function GoogleSignInButton() {
  const { signInWithGoogle } = useAuth();
  const nav = useNavigate();
  const containerRef = useRef(null);
  const clientId = process.env.REACT_APP_GOOGLE_OAUTH_CLIENT_ID;

  useEffect(() => {
    if (!clientId || !containerRef.current) return;
    const SCRIPT_ID = "google-identity-services";
    const initAndRender = () => {
      if (!window.google?.accounts?.id) return;
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: async (response) => {
          if (!response?.credential) return;
          const res = await signInWithGoogle(response.credential, { consent: true });
          if (res.ok) {
            toast.success(`Welcome back, ${res.user.name || res.user.email}`);
            nav("/dashboard");
          } else {
            toast.error(res.error || "Google Sign-In failed");
          }
        },
        auto_select: false,
        cancel_on_tap_outside: true,
      });
      window.google.accounts.id.renderButton(containerRef.current, {
        theme: "outline",
        size: "large",
        shape: "rectangular",
        text: "continue_with",
        logo_alignment: "left",
        width: containerRef.current?.clientWidth || 300,
      });
    };
    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      initAndRender();
      return;
    }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.id = SCRIPT_ID;
    s.onload = initAndRender;
    document.body.appendChild(s);
  }, [clientId, signInWithGoogle, nav]);

  if (!clientId) return null;
  return (
    <div
      ref={containerRef}
      data-testid="google-signin-button"
      className="w-full flex justify-center"
    />
  );
}
